import React, { useCallback, useEffect, useRef, useState } from "react";

import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import { Document, Page } from "react-pdf";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { cn } from "@/utils/ui";

import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";

import { OcrHighlightOverlay } from "./OcrHighlightOverlay";
import { NormalizedRect, SnipOverlay } from "./SnipOverlay";
import { SnipResultPopover } from "./SnipResultPopover";
import { ZOOM_DEFAULT, ZOOM_MAX, ZOOM_MIN, ZOOM_STEP } from "./constants";
import { useContainerWidth } from "./hooks/useContainerWidth";
import { useOcrHighlights } from "./hooks/useOcrHighlights";
import { usePdfPagination } from "./hooks/usePdfPagination";
import { useTextSelection } from "./hooks/useTextSelection";
import { OcrHighlightSource, SnipResult } from "./types";

type PdfPreviewProps = {
  fileUrl: string;
  className?: string;
  snipMode?: boolean;
  onSnipComplete?: (rect: NormalizedRect) => void;
  snipResult?: SnipResult | null;
  snipIsLoading?: boolean;
  snipError?: string | null;
  onSnipResultClose?: () => void;
  onSaveNote?: () => void;
  onMapToReportBuilder?: () => void;
  textSelection?: { text: string; pageNumber?: number } | null;
  onTextSelected?: (text: string, pageNumber?: number) => void;
  onTextSelectionClose?: () => void;
  showZoomControls?: boolean;
  onLoadError?: () => void;
  initialPage?: number;
  /** OCR-based source highlighting for scanned PDFs (MDRS-134). */
  ocrHighlight?: OcrHighlightSource;
};

export const PdfPreview: React.FC<PdfPreviewProps> = ({
  fileUrl,
  className,
  snipMode = false,
  onSnipComplete,
  snipResult,
  snipIsLoading = false,
  snipError,
  onSnipResultClose,
  onSaveNote,
  onMapToReportBuilder,
  textSelection,
  onTextSelected,
  onTextSelectionClose,
  showZoomControls = true,
  onLoadError,
  initialPage,
  ocrHighlight,
}) => {
  const [zoomLevel, setZoomLevel] = useState(ZOOM_DEFAULT);

  const { containerRef, containerWidth } = useContainerWidth();

  const highlightActive = Boolean(ocrHighlight?.highlights.length) && !snipMode;

  // Resolve cited snippets to OCR-line highlight rectangles per page (MDRS-134).
  const { rectsByPage, primaryPage } = useOcrHighlights(
    ocrHighlight?.caseNumber,
    ocrHighlight?.fileName,
    highlightActive ? ocrHighlight?.highlights : undefined
  );
  const hasRects = rectsByPage.size > 0;

  const {
    numPages,
    setNumPages,
    currentPage,
    setCurrentPage,
    pageInputValue,
    setPageInputValue,
    renderedPages,
    setRenderedPages,
    pageRefs,
    handlePreviousPage,
    handleNextPage,
    handlePageInputChange,
    handleUpdateCurrentPage,
  } = usePdfPagination(
    containerRef,
    zoomLevel,
    initialPage,
    // When we have highlight rects, this hook owns the scroll (to the rect);
    // otherwise let pagination do its normal cited-page scroll.
    hasRects
  );

  // Scroll the first highlight rectangle into view once its page has rendered
  // (MDRS-134). Falls back to the pagination page-scroll when there are no
  // rects (OCR not ready / no match).
  const firstRectRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledToRect = useRef(false);
  useEffect(() => {
    hasScrolledToRect.current = false;
  }, [primaryPage, ocrHighlight?.fileName]);
  useEffect(() => {
    if (!hasRects || hasScrolledToRect.current) return;
    if (numPages <= 0 || renderedPages < numPages) return;
    const timer = setTimeout(() => {
      if (firstRectRef.current) {
        firstRectRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        hasScrolledToRect.current = true;
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [hasRects, numPages, renderedPages]);

  useTextSelection({
    containerRef,
    pageRefs,
    snipMode,
    onTextSelected,
  });

  // Reset transient render state on fileUrl change. Page selection is owned by
  // onLoadSuccess (which knows the real page count and the requested cited
  // page, MDRS-122), so it's deliberately not reset here to avoid clobbering
  // the initial page before the new document reports its page count.
  useEffect(() => {
    setRenderedPages(0);
    setZoomLevel(ZOOM_DEFAULT);
  }, [fileUrl]);

  const handleZoomIn = useCallback(
    () => setZoomLevel((z) => Math.min(z + ZOOM_STEP, ZOOM_MAX)),
    []
  );
  const handleZoomOut = useCallback(
    () => setZoomLevel((z) => Math.max(z - ZOOM_STEP, ZOOM_MIN)),
    []
  );

  return (
    <div className="relative grid h-full grid-rows-[1fr_auto] overflow-hidden">
      <div
        className={cn(
          "relative h-full min-h-0 w-full min-w-0 overflow-hidden rounded-lg border bg-background shadow-sm",
          className
        )}
        ref={containerRef}
      >
        {showZoomControls && (
          <div className="absolute right-0 top-0 z-20 flex items-center gap-2 p-1">
            <Button
              title={`Zoom out (${Math.round(zoomLevel * 100)}%)`}
              className="rounded-full p-2"
              onClick={handleZoomOut}
              disabled={zoomLevel <= ZOOM_MIN}
            >
              <ZoomOut className="size-4" />
            </Button>
            <Button
              title={`Zoom in (${Math.round(zoomLevel * 100)}%)`}
              className="rounded-full p-2"
              onClick={handleZoomIn}
              disabled={zoomLevel >= ZOOM_MAX}
            >
              <ZoomIn className="size-4" />
            </Button>
          </div>
        )}

        <ScrollArea className="pdf-container box-border h-full w-full flex-1 overflow-auto">
          <Document
            file={fileUrl}
            onLoadSuccess={({ numPages: n }) => {
              setNumPages(n);
              const startPage =
                initialPage && initialPage > 0 ? Math.min(initialPage, n) : 1;
              setCurrentPage(startPage);
              setPageInputValue(startPage.toString());
              setRenderedPages(0);
              // Only reset scroll to the top when opening on page 1; the
              // initial-page scroll (usePdfPagination) handles cited pages.
              // Skip when highlight rects exist — the rect scroll owns it and
              // would be clobbered by a reset here (MDRS-134).
              if (startPage === 1 && !hasRects) {
                const scrollEl = containerRef.current?.querySelector(
                  "[data-radix-scroll-area-viewport]"
                );
                if (scrollEl) scrollEl.scrollTop = 0;
              }
            }}
            onLoadError={onLoadError}
            loading={
              <div className="flex flex-col items-center gap-2">
                <LoadingIcon className="size-8 animate-spin" />
                <p className="text-sm text-foreground-muted">
                  Loading PDF document...
                </p>
              </div>
            }
            className="pdf-document"
          >
            <div className="flex min-w-full flex-col items-center space-y-4">
              {Array.from({ length: numPages }, (_, i) => (
                <div
                  key={i + 1}
                  ref={(el) => {
                    pageRefs.current[i] = el;
                  }}
                  className="page-container flex w-full justify-center"
                >
                  <div className="relative">
                    <Page
                      pageNumber={i + 1}
                      width={
                        containerWidth > 0
                          ? containerWidth * 0.9 * zoomLevel
                          : undefined
                      }
                      scale={1}
                      renderAnnotationLayer={true}
                      renderTextLayer={!snipMode}
                      loading={
                        <div className="flex justify-center pt-10">
                          <LoadingIcon className="size-6 animate-spin" />
                        </div>
                      }
                      onRenderSuccess={() => {
                        setRenderedPages((prev) => {
                          const newCount = prev + 1;
                          if (newCount === numPages) {
                            setTimeout(() => handleUpdateCurrentPage(), 100);
                          }
                          return newCount;
                        });
                      }}
                    />
                    <OcrHighlightOverlay
                      rects={rectsByPage.get(i + 1) ?? []}
                      firstRectRef={primaryPage === i + 1 ? firstRectRef : undefined}
                    />
                    <SnipOverlay
                      pageNumber={i + 1}
                      isActive={snipMode}
                      onSelectionComplete={(rect) => onSnipComplete?.(rect)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Document>
          <ScrollBar orientation="horizontal" className="z-10" />
          <ScrollBar orientation="vertical" className="z-10" />
        </ScrollArea>
      </div>

      <div>
        {(snipResult || snipIsLoading || snipError) && (
          <SnipResultPopover
            text={snipResult?.text}
            source={snipResult?.source}
            lineCount={snipResult?.lineCount}
            pageNumber={snipResult?.pageNumber}
            isLoading={snipIsLoading}
            isProcessing={snipResult?.isProcessing || false}
            error={snipError || null}
            onClose={() => onSnipResultClose?.()}
            onSaveNote={onSaveNote}
            onMapToReportBuilder={onMapToReportBuilder}
          />
        )}

        {!snipResult && !snipIsLoading && !snipError && textSelection && (
          <SnipResultPopover
            title="Selected Text"
            text={textSelection.text}
            pageNumber={textSelection.pageNumber}
            isLoading={false}
            isProcessing={false}
            error={null}
            onClose={() => onTextSelectionClose?.()}
            onSaveNote={onSaveNote}
            onMapToReportBuilder={onMapToReportBuilder}
          />
        )}

        <div className="flex items-center justify-between p-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground-muted">Page</span>
            <Input
              type="number"
              value={pageInputValue}
              min={1}
              max={numPages}
              onChange={handlePageInputChange}
              onBlur={() => {
                if (!pageInputValue) setPageInputValue(currentPage.toString());
              }}
              className="h-8 w-16 px-2 py-1"
            />
            <span className="text-sm text-foreground-muted">of {numPages}</span>
          </div>

          <div className="flex gap-2">
            <Button
              variant="simple-outline"
              size="sm"
              onClick={handlePreviousPage}
              disabled={currentPage <= 1}
            >
              <ChevronLeft className="size-4" />
              Previous
            </Button>

            <Button
              variant="simple-outline"
              size="sm"
              onClick={handleNextPage}
              disabled={currentPage >= numPages}
            >
              <ChevronRight className="size-4" />
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
